/* Deep space (LANDING_SPEC.md section 3): a few hundred very fine white points on the void, in three
   depth layers that answer the scroll and, slightly, the pointer. One WebGL2 draw call; frames are drawn
   on demand (scroll, pointer, twinkle at a low rate), never in a free-running loop.
   `align` (0..1, set by the scroll in Phase 2) settles the points onto horizontal rows, like strikes on a
   price ladder: space turning into a market. */

const VERT = `#version 300 es
in vec2 aHome;      // 0..1 screen position (x), 0..1 within a tall strip (y)
in float aLayer;    // 0 far, 1 mid, 2 near
in float aSize;     // CSS px
in float aBright;   // 0..1
in float aPhase;    // twinkle phase, < 0 for a star that does not twinkle
in float aRow;      // the ladder row it settles on, 0..1 of the viewport height
uniform vec2 uRes;        // CSS px
uniform float uDpr;
uniform float uScroll;    // CSS px
uniform vec2 uPointer;    // -1..1, smoothed
uniform float uTime;      // s
uniform float uAlign;     // 0..1
uniform float uDim;       // 0..1, the gateway dims space
out float vBright;
out float vSize;
const float PAR[3] = float[3](0.04, 0.10, 0.22);
const float PTR[3] = float[3](3.0, 6.0, 10.0);
void main() {
  int l = int(aLayer + 0.5);
  float strip = uRes.y * 1.6;
  float y = mod(aHome.y * strip - uScroll * PAR[l], strip) - uRes.y * 0.3;
  float x = aHome.x * uRes.x + uPointer.x * PTR[l] + uTime * (0.03 + 0.02 * aLayer);
  x = mod(x, uRes.x + 8.0) - 4.0;
  y += uPointer.y * PTR[l] * 0.6;
  // alignment: the near layer settles first, every point on its row
  float a = clamp(uAlign * 1.25 - (2.0 - aLayer) * 0.12, 0.0, 1.0);
  a = a * a * (3.0 - 2.0 * a);
  y = mix(y, aRow * uRes.y, a);
  vec2 clip = vec2(x / uRes.x * 2.0 - 1.0, 1.0 - y / uRes.y * 2.0);
  gl_Position = vec4(clip, 0.0, 1.0);
  float tw = aPhase < 0.0 ? 1.0 : 1.0 + 0.25 * sin(uTime * 6.2831 / (6.0 + 5.0 * aPhase) + aPhase * 40.0);
  vBright = aBright * tw * (1.0 - 0.7 * uDim);
  vSize = aSize * uDpr;
  gl_PointSize = vSize + 1.0;
}`;

const FRAG = `#version 300 es
precision mediump float;
in float vBright;
in float vSize;
out vec4 o;
void main() {
  float d = length(gl_PointCoord - 0.5) * (vSize + 1.0);
  float a = clamp(vSize * 0.5 + 0.5 - d, 0.0, 1.0) * vBright;
  o = vec4(vec3(a), a);
}`;

export interface Space {
  setScroll(y: number): void;
  setAlign(a: number): void;
  setDim(d: number): void;
  redraw(): void;
  destroy(): void;
}

/** a small seeded random, so the field is the same on every visit */
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
}

export function createSpace(canvas: HTMLCanvasElement, opts: { reduced: boolean }): Space | null {
  const gl = canvas.getContext('webgl2', { antialias: false, alpha: true, premultipliedAlpha: true, powerPreference: 'low-power' });
  if (!gl) return null;
  const sh = (type: number, src: string) => {
    const s = gl.createShader(type)!;
    gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) ?? 'shader');
    return s;
  };
  const prog = gl.createProgram()!;
  gl.attachShader(prog, sh(gl.VERTEX_SHADER, VERT));
  gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FRAG));
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
  gl.useProgram(prog);
  const U = (n: string) => gl.getUniformLocation(prog, n);
  const u = { res: U('uRes'), dpr: U('uDpr'), scroll: U('uScroll'), ptr: U('uPointer'), time: U('uTime'), align: U('uAlign'), dim: U('uDim') };

  // the field: count by viewport area, a long tail of brightness (a few bright, most faint)
  const area = innerWidth * innerHeight;
  const mobile = innerWidth < 768;
  const n = Math.round(Math.min(mobile ? 220 : 600, Math.max(mobile ? 140 : 160, (area / (1440 * 900)) * 420)));
  const r = rng(20261007);
  const STRIDE = 8;
  const data = new Float32Array(n * STRIDE);
  for (let i = 0; i < n; i++) {
    const q = r();
    const layer = q < 0.55 ? 0 : q < 0.85 ? 1 : 2;
    const b = r();
    data.set([
      r(), r(), layer,
      0.6 + 0.9 * Math.pow(r(), 2.2) + layer * 0.12,
      0.18 + 0.67 * Math.pow(b, 3),
      r() < 1 / 40 ? r() : -1,
      // rows like strikes: spaced 18 to 26 px apart at 900 px, denser toward the middle
      Math.round((0.12 + 0.76 * (0.5 + (r() - 0.5) * Math.abs(r() * 2 - 1))) * 40) / 40,
      0,
    ], i * STRIDE);
  }
  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
  const attr = (name: string, size: number, offset: number) => {
    const loc = gl.getAttribLocation(prog, name);
    if (loc < 0) return;
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, size, gl.FLOAT, false, STRIDE * 4, offset * 4);
  };
  attr('aHome', 2, 0); attr('aLayer', 1, 2); attr('aSize', 1, 3); attr('aBright', 1, 4); attr('aPhase', 1, 5); attr('aRow', 1, 6);
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

  let scroll = 0, align = 0, dim = 0, raf = 0, dead = false;
  const ptr = { x: 0, y: 0, tx: 0, ty: 0 };
  const t0 = performance.now();

  const size = () => {
    const dpr = Math.min(devicePixelRatio || 1, 2);
    canvas.width = Math.round(innerWidth * dpr);
    canvas.height = Math.round(innerHeight * dpr);
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.uniform2f(u.res, innerWidth, innerHeight);
    gl.uniform1f(u.dpr, dpr);
  };
  const draw = () => {
    raf = 0;
    if (dead) return;
    // the pointer eases toward its target (a critically damped approach, settles in about 600 ms)
    ptr.x += (ptr.tx - ptr.x) * 0.12; ptr.y += (ptr.ty - ptr.y) * 0.12;
    const t = opts.reduced ? 0 : (performance.now() - t0) / 1000;
    gl.uniform1f(u.scroll, scroll);
    gl.uniform2f(u.ptr, ptr.x, ptr.y);
    gl.uniform1f(u.time, t);
    gl.uniform1f(u.align, align);
    gl.uniform1f(u.dim, dim);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.POINTS, 0, n);
    if (Math.abs(ptr.tx - ptr.x) > 0.002 || Math.abs(ptr.ty - ptr.y) > 0.002) request();
  };
  const request = () => { if (!raf && !dead) raf = requestAnimationFrame(draw); };

  size();
  request();
  const onResize = () => { size(); request(); };
  const onPointer = (e: PointerEvent) => {
    if (opts.reduced || e.pointerType !== 'mouse') return;
    ptr.tx = (e.clientX / innerWidth) * 2 - 1; ptr.ty = (e.clientY / innerHeight) * 2 - 1; request();
  };
  addEventListener('resize', onResize);
  addEventListener('pointermove', onPointer, { passive: true });
  // drift and twinkle: a slow tick (12 fps is plenty for motion this slow), paused when hidden
  const tick = opts.reduced ? 0 : window.setInterval(() => { if (!document.hidden) request(); }, 83);

  return {
    setScroll(y) { scroll = y; request(); },
    setAlign(a) { align = a; request(); },
    setDim(d) { dim = d; request(); },
    redraw: request,
    destroy() {
      dead = true; cancelAnimationFrame(raf); clearInterval(tick);
      removeEventListener('resize', onResize); removeEventListener('pointermove', onPointer);
    },
  };
}
